import React from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import Row from 'react-bootstrap/Row';
import axios from 'axios';
import * as yup from 'yup';
import {Formik} from 'formik';
import {useStoreActions, useStoreState} from 'easy-peasy';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

interface EmailFetchCredentials {
    id: number;
    email: string;
    password: string;
    rev?: string;
}

interface FormValues {
    email: string;
    password: string;
}

interface EmailFetchCredentialsSettingsFormProps {
    objId?: number;
    onSave?: () => void;
}

const emailFetchCredentialsSchema = yup.object({
    email: yup.string()
        .email(i18n.t('jt_email_valid.text'))
        .max(255, i18n.t('jt_email_max_length_255.text'))
        .required(i18n.t('jt_email_required.text')),
    password: yup.string(),
});

const EmailFetchCredentialsSettingsForm: React.FC<EmailFetchCredentialsSettingsFormProps> = React.memo((
    {
        objId = 0,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const setSettingsModalOpen = useStoreActions((actions: any) => actions.settingsModalOpen.setSettingsModalOpen);

    const credentialsList = useStoreState((state: any) => state.emailFetchCredentials.items);

    const addOrEditAction = useStoreActions((actions: any) => actions.emailFetchCredentials.addOrEdit);

    const onSubmit = async (values: FormValues) => {
        let requestMethod: (url: string, data: FormValues) => Promise<any>;
        let url = window.URLS.emailFetchCredentialsResource;
        if (objId) {
            url = `${url}/${objId}`;
            requestMethod = axios.put;
        } else {
            requestMethod = axios.post;
        }

        const response = await requestMethod(url, values);
        const additionalData: { rev?: string } = {};
        if (Object.keys(values).includes('password')) {
            additionalData.rev = Math.random().toString();
        }

        setSettingsModalOpen(false);
        addOrEditAction({
            ...response.data,
            ...additionalData,
        });

        if (objId) {
            showSuccess(i18n.t('to_email_fetch_ch.text'));
        } else {
            showSuccess(i18n.t('to_email_fetch_cr.text'));
        }

        onSave?.();
    };

    return (
        <Formik
            initialValues={credentialsList.find((el: EmailFetchCredentials) => el.id === objId) || {
                email: '',
                password: '',
            }}
            onSubmit={onSubmit}
            validationSchema={emailFetchCredentialsSchema}
        >
            {(formik) => (
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="email" >
                                <Form.Label>{i18n.t('jt_email_address.text')}</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    name="email"
                                    isInvalid={formik.touched.email && !!formik.errors.email}
                                    autoFocus
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="password" >
                                <Form.Label> {i18n.t('jt_password.text')}</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="password"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.password}
                                    isInvalid={formik.touched.password && !!formik.errors.password}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.password}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Form.Group className="ml-auto mb-3" controlId="action-save">
                            <Button
                                variant="info"
                                fullWidth
                                type="submit"
                                data-cy="email-fetch-credentials-settings-info-btn">
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Form.Group>
                    </Row>
                </Form >
            )}
        </Formik >
    );
});


export default EmailFetchCredentialsSettingsForm; 